/**
 * An organisation's record and roster, by ONDO id.
 */
import type {
  CreateEntityRequest,
  CreateEntityResponse,
  InviteColleagueRequest,
  OrgProfileResponse,
  SetMembershipRoleRequest,
  SetRoleBundleRequest,
  SetSeatPrivilegeRequest,
  UpdateOrgRequest,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

/** An organisation's record and its active roster, by ONDO id. */
export const fetchOrgProfile = (ondoId: string) =>
  request<OrgProfileResponse>(`/orgs/${encodeURIComponent(ondoId)}`);

/** Admin only; the server refuses anyone else. Replace semantics, as above. */
export const updateOrg = (ondoId: string, body: UpdateOrgRequest) =>
  requestWith<OrgProfileResponse>("PUT", `/orgs/${encodeURIComponent(ondoId)}`, body);

/**
 * Ends a colleague's seat. An admin of that firm only; the server refuses
 * anyone else, and refuses the firm's last administrator.
 *
 * Answers with the whole profile rather than nothing, and the reason is
 * `canEnd`: dropping to one administrator turns that flag false on the
 * remaining admin's own row, so the roster the caller holds has to be replaced
 * rather than patched.
 */
export const endMembership = (ondoId: string, membershipId: string) =>
  requestWith<OrgProfileResponse>(
    "DELETE",
    `/orgs/${encodeURIComponent(ondoId)}/members/${encodeURIComponent(membershipId)}`,
  );

/**
 * Sets or clears a seat's named role — OND-61.
 *
 * An administrator holding staff administration only; the server refuses
 * anyone else, with one sentence covering "no such seat", "not your firm" and
 * "you lack staff administration".
 *
 * Answers with the whole profile, for `endMembership`'s reason carried one step
 * further: a role change moves that seat's RESOLVED abilities, and resolving
 * them in the client would be the second definition of the four-layer rule that
 * `holdsPrivilege` exists not to be.
 */
export const setMembershipRole = (
  ondoId: string,
  membershipId: string,
  body: SetMembershipRoleRequest,
) =>
  requestWith<OrgProfileResponse>(
    "PUT",
    `/orgs/${encodeURIComponent(ondoId)}/members/${encodeURIComponent(membershipId)}/role`,
    body,
  );

/**
 * Grants, refuses or CLEARS one ability on one seat.
 *
 * `granted: null` clears the exception so the seat's bundle answers again —
 * the third state, which is why this is not a boolean toggle.
 *
 * The server names the refusal when the ability needs a more senior seat, and
 * is opaque about everything else.
 */
export const setSeatPrivilege = (
  ondoId: string,
  membershipId: string,
  body: SetSeatPrivilegeRequest,
) =>
  requestWith<OrgProfileResponse>(
    "PUT",
    `/orgs/${encodeURIComponent(ondoId)}/members/${encodeURIComponent(membershipId)}/privileges`,
    body,
  );

/**
 * Edits or clears one line of one of the firm's named-role bundles.
 *
 * Not scoped under a member, because it is not about one: an edit here reaches
 * every seat at this firm holding that role, including seats nobody has hired
 * yet.
 */
export const setRoleBundle = (ondoId: string, body: SetRoleBundleRequest) =>
  requestWith<OrgProfileResponse>("PUT", `/orgs/${encodeURIComponent(ondoId)}/bundles`, body);

/**
 * Invites one address to one seat — OND-24.
 *
 * `admin` plus `staff_admin` only, which is the same door that ends a seat, and
 * the server refuses anyone else with one sentence covering "not your firm",
 * "your own address" and "already invited" — none of which can vary with
 * whether that address belongs to an ONDO account.
 *
 * THE INVITATION EMAIL IS NOT SENT BY THIS REQUEST. The row queues it inside its
 * own transaction and a worker delivers it, because a request path cannot write
 * to an address nobody has authenticated — the gap `20261003093000` closed for
 * the confirmation ask and `20261004090000` closed again for the buyer.
 *
 * Answers with the whole profile, like every write on this resource: the
 * invitation list is part of it, and the firm's own copy of what it has sent is
 * the only record anybody is given.
 */
export const inviteColleague = (ondoId: string, body: InviteColleagueRequest) =>
  request<OrgProfileResponse>(`/orgs/${encodeURIComponent(ondoId)}/invitations`, body);

/**
 * Takes an unanswered invitation back — OND-24.
 *
 * The authority is asked again at this moment rather than remembered from the
 * invitation, so an administrator who has since lost staff administration cannot
 * reach into the firm's pending offers. Nothing is deleted: the row keeps its
 * outcome, because a withdrawn offer is part of how this firm's staff came to be
 * what it is.
 */
export const withdrawInvitation = (ondoId: string, invitationId: string) =>
  requestWith<OrgProfileResponse>(
    "DELETE",
    `/orgs/${encodeURIComponent(ondoId)}/invitations/${encodeURIComponent(invitationId)}`,
  );

/**
 * Creates an entity the caller owns — OND-52.
 *
 * THE ONE WRITE ON THIS RESOURCE THAT DOES NOT ANSWER WITH A PROFILE, because
 * there is no profile the caller is already holding: the thing it makes did not
 * exist a moment ago. What comes back is the minted ONDO id, which is what names
 * the entity on the next act — creating a collection it owns.
 *
 * It also changes the caller's own account, since they are seated at it, so
 * whatever called this refetches `/me` and hands the answer to `replaceMe`
 * rather than patching a copy of the memberships.
 */
export const createEntity = (body: CreateEntityRequest) =>
  request<CreateEntityResponse>("/orgs", body);
